/**
 * Serialised cleanup of committed dictation utterances.
 *
 * Each utterance is written raw the moment it is transcribed, then replaced by
 * its cleaned form when the server answers. Those answers land out of band and
 * out of order relative to speech, which is what this queue exists to manage:
 *
 * - **One at a time.** Rewriting an utterance moves every span recorded after
 *   it. Running two cleanups concurrently would have the second computing its
 *   splice against offsets the first is about to invalidate.
 * - **Offsets are repaired, not recomputed.** When a rewrite changes length,
 *   every queued span at a higher offset *in the same field* shifts by the same
 *   amount. Spans in other fields are untouched — offsets only mean anything
 *   within the field they were recorded in.
 * - **The physician always wins.** A span is only rewritten while the field
 *   still holds exactly the text we put there — checked once before the request
 *   and again after it, since an utterance takes seconds during which they may
 *   have typed over it. Anything else silently keeps the raw text.
 *
 * Each entry carries its own target, because a physician dictating into a form
 * moves between fields mid-recording: the utterance has to be polished in the
 * field it was spoken into, not in whichever one is current when the answer
 * arrives. `T` is the caller's handle on a field (the element, in the portal);
 * the queue only ever compares targets by identity and hands them back.
 */
import {
  isRegionIntact,
  replaceRegion,
  shiftRegion,
  type TranscriptRegion,
} from "~/utils/dictationTranscript";

/** A span, and the field it lives in. */
export interface TargetedRegion<T> {
  target: T;
  region: TranscriptRegion;
}

export interface CleanupQueueDeps<T> {
  /** Ask the server for the cleaned form. Must resolve to the raw text on failure. */
  clean: (utterance: string, precedingText: string) => Promise<string>;
  /** Current text of `target`, or null once that field is gone. */
  readField: (target: T) => string | null;
  /**
   * Commit a rewritten value to `target`. `at`/`shift`/`newLength` describe the
   * edit so the caller can move anything it tracks by offset in that field —
   * the open utterance's span, the caret — including clamping a caret that sat
   * inside the span.
   */
  writeField: (
    target: T,
    value: string,
    at: number,
    shift: number,
    newLength: number,
  ) => void;
}

export class DictationCleanupQueue<T> {
  private pending: TargetedRegion<T>[] = [];
  private chain: Promise<void> = Promise.resolve();
  /**
   * Bumped by `cancel()`. A step that started before the bump discards its
   * answer instead of writing it — otherwise a cleanup in flight when the form
   * is saved rewrites a field the physician has already committed and left.
   */
  private generation = 0;

  constructor(private readonly deps: CleanupQueueDeps<T>) {}

  /** Queue the span holding a just-committed utterance for cleanup. */
  enqueue(entry: TargetedRegion<T>): void {
    this.pending.push(entry);
    // `clean` resolves rather than throws, but a rejection here would poison
    // the chain and silently drop every later utterance — so absorb it.
    this.chain = this.chain.then(() => this.step()).catch(() => undefined);
  }

  /** Abandon queued work — a new dictation session, or the field going away. */
  clear(): void {
    this.pending = [];
  }

  /**
   * Abandon queued work *and* disown whatever is already in flight, so nothing
   * this queue started can still write after the caller has moved on.
   */
  cancel(): void {
    this.clear();
    this.generation++;
  }

  /** Resolves once the queue has drained and nothing new was appended. */
  async idle(): Promise<void> {
    let settled = this.chain;
    for (;;) {
      await settled;
      // A step that ran while we waited may have queued more work behind it.
      if (settled === this.chain) return;
      settled = this.chain;
    }
  }

  private async step(): Promise<void> {
    const entry = this.pending.shift();
    if (!entry) return;
    const { target, region } = entry;
    const generation = this.generation;

    const before = this.deps.readField(target);
    if (before === null || !isRegionIntact(before, region)) return;

    const utterance = region.text.trim();
    if (!utterance) return;

    const cleaned = await this.deps.clean(
      utterance,
      before.slice(0, region.start),
    );
    if (generation !== this.generation) return;

    // Re-read: the request took seconds, and the field is live throughout.
    const after = this.deps.readField(target);
    if (after === null) return;

    const replacement = replaceRegion(after, region, cleaned);
    // Null means they typed over it while we were waiting; an identical span
    // means the utterance already read correctly and there is nothing to write.
    if (!replacement || replacement.region.text === region.text) return;

    this.deps.writeField(
      target,
      replacement.value,
      region.start,
      replacement.shift,
      replacement.region.text.length,
    );
    this.pending = this.pending.map((queued) =>
      queued.target === target
        ? {
            ...queued,
            region: shiftRegion(queued.region, region.start, replacement.shift),
          }
        : queued,
    );
  }
}
